import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail, formatINR } from "@/api/client";
import { toast } from "sonner";
import { Store, Plus, Edit, Trash2, Save, X, Star, Eye, EyeOff, Search, Package, PackageX, PackagePlus, Check } from "lucide-react";
import ImageUploader from "@/components/upload/ImageUploader";

const empty = {
  name: "",
  slug: "",
  logo_url: "",
  description: "",
  featured: false,
  active: true,
  display_order: 0,
};

const AdminBrands = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(null); // { mode: 'add'|'edit', brand?}
  const [productsModal, setProductsModal] = useState(null); // {brand}
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/brands");
      setBrands(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setForm(empty);
    setModal({ mode: "add" });
  };
  const openEdit = (b) => {
    setForm({
      name: b.name || "",
      slug: b.slug || "",
      logo_url: b.logo_url || "",
      description: b.description || "",
      featured: !!b.featured,
      active: b.active !== false,
      display_order: b.display_order || 0,
    });
    setModal({ mode: "edit", brand: b });
  };
  const close = () => { setModal(null); setForm(empty); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Brand name is required"); return; }
    setBusy(true);
    try {
      if (modal.mode === "add") {
        await api.post("/admin/brands", form);
        toast.success("Brand added ✓");
      } else {
        await api.put(`/admin/brands/${modal.brand.id}`, form);
        toast.success("Brand updated ✓");
      }
      close();
      load();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const del = async (b) => {
    if (!window.confirm(`Delete brand "${b.name}"?\nProducts assigned to this brand will remain intact.`)) return;
    try {
      await api.delete(`/admin/brands/${b.id}`);
      toast.success("Brand deleted");
      load();
    } catch (ex) {
      toast.error("Delete failed");
    }
  };

  const toggleField = async (b, field) => {
    try {
      await api.put(`/admin/brands/${b.id}`, {
        name: b.name, slug: b.slug, logo_url: b.logo_url || "", description: b.description || "",
        featured: field === "featured" ? !b.featured : b.featured,
        active: field === "active" ? !b.active : b.active,
        display_order: b.display_order || 0,
      });
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const filtered = brands.filter((b) => !q || b.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="admin-brands">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center gap-2 bg-white border border-border rounded-lg px-3 py-2 flex-1 max-w-md">
          <Search className="w-4 h-4 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search brands…"
            className="flex-1 text-sm focus:outline-none"
            data-testid="admin-brand-search"
          />
        </div>
        <button
          onClick={openAdd}
          className="bg-secondary text-secondary-foreground text-sm font-bold px-4 py-2 rounded-lg flex items-center gap-1 ml-auto"
          data-testid="admin-btn-add-brand"
        >
          <Plus className="w-4 h-4" /> Add Brand
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading brands…</div>
      ) : filtered.length === 0 ? (
        <div className="card-fah p-8 text-center text-muted-foreground">
          <Store className="w-10 h-10 mx-auto mb-2 opacity-40" />
          No brands yet. Click <b>Add Brand</b> to create one.
        </div>
      ) : (
        <div className="card-fah overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                {["Order", "Brand", "Slug", "Products", "Featured", "Active", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-3 text-xs uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => (
                <tr key={b.id} className="border-t border-border" data-testid={`admin-brand-row-${b.slug}`}>
                  <td className="px-4 py-3 text-muted-foreground w-16">#{b.display_order}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-accent/40 border border-border overflow-hidden flex items-center justify-center shrink-0">
                        {b.logo_url ? (
                          <img src={b.logo_url} alt={b.name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="font-bold text-primary text-sm">{b.name.charAt(0).toUpperCase()}</span>
                        )}
                      </div>
                      <div>
                        <div className="font-semibold">{b.name}</div>
                        {b.description && <div className="text-xs text-muted-foreground line-clamp-1 max-w-[240px]">{b.description}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{b.slug}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setProductsModal({ brand: b })}
                      className="inline-flex items-center gap-1 bg-muted/60 hover:bg-primary/10 hover:text-primary text-foreground text-xs font-semibold px-2 py-1 rounded transition"
                      data-testid={`open-products-${b.slug}`}
                    >
                      <Package className="w-3 h-3" />
                      {b.product_count}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleField(b, "featured")}
                      className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded ${b.featured ? "bg-secondary text-secondary-foreground" : "bg-muted text-muted-foreground"}`}
                      data-testid={`toggle-featured-${b.slug}`}
                    >
                      <Star className={`w-3 h-3 ${b.featured ? "fill-current" : ""}`} />
                      {b.featured ? "Featured" : "Feature"}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleField(b, "active")}
                      className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded ${b.active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}
                      data-testid={`toggle-active-${b.slug}`}
                    >
                      {b.active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      {b.active ? "Visible" : "Hidden"}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button
                        onClick={() => setProductsModal({ brand: b })}
                        className="p-1.5 rounded bg-primary/10 hover:bg-primary/20 text-primary"
                        data-testid={`manage-products-${b.slug}`}
                        title="Manage products"
                      >
                        <Package className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => openEdit(b)}
                        className="p-1.5 rounded bg-muted hover:bg-muted/70 text-foreground"
                        data-testid={`edit-brand-${b.slug}`}
                        title="Edit"
                      >
                        <Edit className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => del(b)}
                        className="p-1.5 rounded bg-red-50 hover:bg-red-100 text-red-700"
                        data-testid={`delete-brand-${b.slug}`}
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {modal && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={close}
        >
          <div
            className="bg-white w-full max-w-2xl rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="admin-brand-modal"
          >
            <div className="sticky top-0 bg-white border-b border-border px-5 py-3 flex items-center justify-between z-10">
              <h3 className="font-display font-bold">
                {modal.mode === "add" ? "Add New Brand" : `Edit "${modal.brand.name}"`}
              </h3>
              <button onClick={close} className="p-1 rounded hover:bg-muted"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-5 space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <Labeled label="Brand Name *">
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white"
                    data-testid="brand-name-input"
                    placeholder="e.g. Bayer"
                  />
                </Labeled>
                <Labeled label="Slug (URL) — auto if empty">
                  <input
                    value={form.slug}
                    onChange={(e) => setForm({ ...form, slug: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white font-mono"
                    data-testid="brand-slug-input"
                    placeholder="e.g. bayer"
                  />
                </Labeled>
              </div>

              <Labeled label="Description (shown on brand page)">
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white"
                  placeholder="Short intro about the brand and its products."
                />
              </Labeled>

              <Labeled label="Brand Logo">
                <ImageUploader
                  value={form.logo_url ? [form.logo_url] : []}
                  onChange={(imgs) => setForm({ ...form, logo_url: imgs[0] || "" })}
                  maxFiles={1}
                />
              </Labeled>

              <div className="grid grid-cols-3 gap-3">
                <Labeled label="Display Order">
                  <input
                    type="number"
                    value={form.display_order}
                    onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value || "0", 10) })}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white"
                  />
                </Labeled>
                <label className="flex items-center gap-2 text-sm mt-6">
                  <input
                    type="checkbox"
                    checked={form.featured}
                    onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                    data-testid="brand-featured-toggle"
                  />
                  <Star className="w-4 h-4 text-secondary" />
                  Featured
                </label>
                <label className="flex items-center gap-2 text-sm mt-6">
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                    data-testid="brand-active-toggle"
                  />
                  <Eye className="w-4 h-4 text-green-600" />
                  Visible on site
                </label>
              </div>

              <div className="text-xs text-muted-foreground bg-muted/40 p-3 rounded-lg">
                <b>Note:</b> Products get linked to this brand automatically when their <code>brand</code> field
                matches this brand's name (case-insensitive). Sellers enter brand name in the product form.
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-border px-5 py-3 flex justify-end gap-2">
              <button onClick={close} className="px-4 py-2 border border-border rounded-lg text-sm font-semibold">
                Cancel
              </button>
              <button
                onClick={save}
                disabled={busy}
                className="bg-primary text-white px-5 py-2 rounded-lg font-bold flex items-center gap-1 disabled:opacity-50"
                data-testid="brand-save-btn"
              >
                <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Products Manager Modal */}
      {productsModal && (
        <BrandProductsManager
          brand={productsModal.brand}
          onClose={() => { setProductsModal(null); load(); }}
        />
      )}
    </div>
  );
};

/* ----------------------------- Products Manager ----------------------------- */
const BrandProductsManager = ({ brand, onClose }) => {
  const [assigned, setAssigned] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/brands/${brand.id}/products`);
      setAssigned(data.products || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [brand.id]);

  const searchProducts = async (q) => {
    setSearching(true);
    try {
      const { data } = await api.get("/admin/products/lite", {
        params: { q: q || undefined, exclude_brand: brand.name, limit: 60 },
      });
      setCandidates(data);
    } finally { setSearching(false); }
  };

  useEffect(() => {
    if (!addOpen) return;
    const t = setTimeout(() => searchProducts(searchQ), 250);
    return () => clearTimeout(t);
  }, [searchQ, addOpen]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const assignSelected = async () => {
    if (selectedIds.size === 0) { toast.error("Select at least one product"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/admin/brands/${brand.id}/products`, {
        product_ids: Array.from(selectedIds),
      });
      toast.success(`${r.data.updated} product(s) added to ${brand.name} ✓`);
      setSelectedIds(new Set());
      setAddOpen(false);
      setSearchQ("");
      load();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || "Assign failed");
    } finally { setBusy(false); }
  };

  const removeProduct = async (p) => {
    if (!window.confirm(`Remove "${p.name}" from ${brand.name}?\nThe product will remain on the site but unassigned from any brand.`)) return;
    try {
      await api.delete(`/admin/brands/${brand.id}/products/${p.id}`);
      toast.success("Removed from brand");
      load();
    } catch (ex) {
      toast.error("Remove failed");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-3xl rounded-t-2xl sm:rounded-2xl max-h-[95vh] overflow-y-auto flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="brand-products-modal"
      >
        <div className="sticky top-0 bg-white border-b border-border px-5 py-3 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-accent/40 border border-border overflow-hidden flex items-center justify-center shrink-0">
              {brand.logo_url ? (
                <img src={brand.logo_url} alt={brand.name} className="w-full h-full object-cover" />
              ) : (
                <span className="font-bold text-primary">{brand.name.charAt(0).toUpperCase()}</span>
              )}
            </div>
            <div>
              <h3 className="font-display font-bold">{brand.name}</h3>
              <div className="text-xs text-muted-foreground">Manage products under this brand</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Assigned products */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-sm">
                Products under this brand ({assigned.length})
              </h4>
              <button
                onClick={() => setAddOpen((v) => !v)}
                className="bg-secondary text-secondary-foreground text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1"
                data-testid="btn-add-products-to-brand"
              >
                <PackagePlus className="w-3.5 h-3.5" /> {addOpen ? "Close" : "Add Products"}
              </button>
            </div>

            {loading ? (
              <div className="text-sm text-muted-foreground py-4 text-center">Loading…</div>
            ) : assigned.length === 0 ? (
              <div className="card-fah p-6 text-center text-muted-foreground text-sm">
                <Package className="w-8 h-8 mx-auto mb-2 opacity-40" />
                No products assigned yet. Click <b>Add Products</b> to link products to <b>{brand.name}</b>.
              </div>
            ) : (
              <div className="border border-border rounded-lg divide-y divide-border">
                {assigned.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 p-3" data-testid={`assigned-product-${p.id}`}>
                    <img
                      src={p.images?.[0] || ""}
                      alt=""
                      className="w-12 h-12 rounded object-cover bg-muted shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold line-clamp-1">{p.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {p.category} · {p.seller_name} · {formatINR(p.price)}
                      </div>
                    </div>
                    <button
                      onClick={() => removeProduct(p)}
                      className="p-2 rounded bg-red-50 hover:bg-red-100 text-red-700 shrink-0"
                      title="Remove from brand"
                      data-testid={`remove-product-${p.id}`}
                    >
                      <PackageX className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add products picker */}
          {addOpen && (
            <div className="border border-primary/30 bg-primary/5 rounded-lg p-4" data-testid="add-products-picker">
              <div className="flex items-center gap-2 bg-white border border-border rounded-lg px-3 py-2 mb-3">
                <Search className="w-4 h-4 text-muted-foreground" />
                <input
                  autoFocus
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  placeholder="Search products by name or current brand…"
                  className="flex-1 text-sm focus:outline-none"
                  data-testid="brand-product-search"
                />
              </div>
              <div className="text-xs text-muted-foreground mb-2">
                Showing products <b>not yet</b> under <b>{brand.name}</b>. Select and click <b>Assign</b>.
              </div>
              <div className="max-h-72 overflow-y-auto border border-border rounded-lg divide-y divide-border bg-white">
                {searching ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">Searching…</div>
                ) : candidates.length === 0 ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">No matching products</div>
                ) : (
                  candidates.map((p) => {
                    const sel = selectedIds.has(p.id);
                    return (
                      <label
                        key={p.id}
                        className={`flex items-center gap-3 p-2.5 cursor-pointer transition ${sel ? "bg-primary/10" : "hover:bg-muted/40"}`}
                        data-testid={`candidate-${p.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={sel}
                          onChange={() => toggleSelect(p.id)}
                          className="w-4 h-4"
                        />
                        <img src={p.images?.[0] || ""} alt="" className="w-10 h-10 rounded object-cover bg-muted shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold line-clamp-1">{p.name}</div>
                          <div className="text-[11px] text-muted-foreground">
                            {p.brand ? `Currently: ${p.brand}` : "No brand"} · {p.category} · {formatINR(p.price)}
                          </div>
                        </div>
                        {sel && <Check className="w-4 h-4 text-primary shrink-0" />}
                      </label>
                    );
                  })
                )}
              </div>
              <div className="flex items-center justify-between mt-3">
                <div className="text-xs text-muted-foreground">
                  {selectedIds.size} selected
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setSelectedIds(new Set()); setAddOpen(false); setSearchQ(""); }}
                    className="px-3 py-1.5 border border-border rounded-lg text-xs font-semibold bg-white"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={assignSelected}
                    disabled={busy || selectedIds.size === 0}
                    className="bg-primary text-white px-4 py-1.5 rounded-lg text-xs font-bold disabled:opacity-50 flex items-center gap-1"
                    data-testid="btn-assign-selected"
                  >
                    <PackagePlus className="w-3.5 h-3.5" />
                    {busy ? "Assigning…" : `Assign ${selectedIds.size ? `(${selectedIds.size})` : ""}`}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="text-xs text-muted-foreground bg-muted/40 p-3 rounded-lg">
            <b>How it works:</b> Assigning a product to <b>{brand.name}</b> sets its <code>brand</code> field to
            "{brand.name}". Removing sets it back to empty. Product data and seller ownership stay unchanged.
          </div>
        </div>
      </div>
    </div>
  );
};

const Labeled = ({ label, children }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

export default AdminBrands;
